// Typescript CodeGen Prototype "Expectant Sunshine"
// Transform Version: v1.0.0
// Template Version:  v1.0.0
//
// -- DO NOT MODIFY THIS FILE! --

/* tslint:disable:no-trailing-whitespace */

import * as _ from 'lodash';
import * as Wellspring from '@greenheck/wellspring';
import * as Axon from '@jlekie/axon';
import { v4 as Uuid } from 'uuid';
import * as ParseHelpers from '@jlekie/parse-helpers';
import * as Zod from 'zod';

export enum MappingDirections {
    To,
    From,
    Both
}

export function isMapperModel(source: any): source is IMapper {
    // Method Implementation: simple
    return source.modelName === 'Mapper';
}

export function isMappingModel(source: any): source is IMapping {
    // Method Implementation: simple
    return source.modelName === 'Mapping';
}

export interface MapperZodSchemaShape {
    mappings?: MappingZodSchemaShape[];
}

export interface MappingZodSchemaShape {
    source?: string;

    destination?: string;

    direction?: 'To' | 'From' | 'Both';

    restrict?: boolean;

    mappings?: MappingZodSchemaShape[];
}

// Artifact Implementation: artifacts/zodSchemaType
export const MapperZodSchema: Zod.ZodSchema<MapperZodSchemaShape> = Zod.lazy(() => Zod.object({
    mappings: Zod.array(MappingZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const MappingZodSchema: Zod.ZodSchema<MappingZodSchemaShape> = Zod.lazy(() => Zod.object({
    source: Zod.string().optional(),
    destination: Zod.string().optional(),
    direction: Zod.enum([ 'To', 'From', 'Both' ]).optional(),
    restrict: Zod.boolean().optional(),
    mappings: Zod.array(MappingZodSchema).optional()
}).nonstrict());

export interface IMapper extends Wellspring.IModel {
    readonly modelName: string;

    readonly mappings: Mapping[];

    readonly isMappingsDefined: boolean;
}

export interface IMapping extends Wellspring.IModel {
    readonly modelName: string;

    readonly source: string;

    readonly destination: string;

    readonly direction: MappingDirections;

    readonly restrict: boolean;

    readonly mappings: Mapping[];

    readonly isSourceDefined: boolean;

    readonly isDestinationDefined: boolean;

    readonly isDirectionDefined: boolean;

    readonly isRestrictDefined: boolean;

    readonly isMappingsDefined: boolean;
}

export class Mapper implements IMapper {
    public static readonly modelName: string = 'Mapper';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'mappings' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Mapper.modelName;
    }

    private _mappings: Mapping[];

    public get mappings(): Mapping[] {
        // Method Implementation: simple
        return this._mappings;
    }

    public set mappings(value: Mapping[]) {
        // Method Implementation: simple
        this._mappings = value;
        this._isMappingsDefined = true;
    }

    private _isMappingsDefined: boolean;

    public get isMappingsDefined(): boolean {
        // Method Implementation: simple
        return this._isMappingsDefined;
    }

    public constructor();
    public constructor(source: IMapper);
    public constructor(source: Partial<Pick<Mapper, 'mappings'>>);
    public constructor(source?: IMapper | Partial<Pick<Mapper, 'mappings'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isMapperModel(source)) {
                if (source.isMappingsDefined) {
                    this._mappings = source.mappings;
                    this._isMappingsDefined = true;
                }
                else {
                    this._mappings = [];
                    this._isMappingsDefined = true;
                }
            }
            else {
                if (source.mappings !== undefined) {
                    this._mappings = source.mappings;
                    this._isMappingsDefined = true;
                }
                else {
                    this._mappings = [];
                    this._isMappingsDefined = true;
                }
            }
        }
        else {
            this._mappings = [];
            this._isMappingsDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Mapper.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isMappingsDefined && this.mappings !== undefined)
            definedProperties.push('mappings');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'mappings':
                return this.mappings;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isMappingsDefined && this.mappings !== undefined) {
            if (this.mappings !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('mappings', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.mappings.length));
                for (let i = 0; i < this.mappings.length; i++) {
                    const item = this.mappings[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('mappings', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'mappings':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.mappings = [];
                
                    let item: Mapping = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Mapping.modelName) {
                                item = new Mapping();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Mapping.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'mappings' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'mappings'`);
                        }
                
                        this.mappings.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'mappings' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'mappings'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // mappings
        if (this.isMappingsDefined && this.mappings !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.mappings === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.mappings.length);
            
                for (const item of this.mappings) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // mappings
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Mapping;
            
                    this.mappings = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Mapping();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.mappings.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Mapper {
        // Method Implementation: simple
        const schema = MapperZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: MapperZodSchemaShape): Mapper {
        // Method Implementation: parseZodSchema
        return new Mapper({
            mappings: schema.mappings?.map(i => Mapping.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            mappings: this.isMappingsDefined ? (this.mappings?.map(item => item)) : undefined,
        };
    }

    public clone(): Mapper {
        // Method Implementation: clone
        return new Mapper({
            mappings: this.isMappingsDefined ? this.mappings?.map(i => i.clone()) : undefined
        });
    }
}

export class Mapping implements IMapping {
    public static readonly modelName: string = 'Mapping';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'source', 'destination', 'direction', 'restrict', 'mappings' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Mapping.modelName;
    }

    private _source: string;

    private _destination: string;

    private _direction: MappingDirections;

    private _restrict: boolean;

    private _mappings: Mapping[];

    public get source(): string {
        // Method Implementation: simple
        return this._source;
    }

    public get destination(): string {
        // Method Implementation: simple
        return this._destination;
    }

    public get direction(): MappingDirections {
        // Method Implementation: simple
        return this._direction;
    }

    public get restrict(): boolean {
        // Method Implementation: simple
        return this._restrict;
    }

    public get mappings(): Mapping[] {
        // Method Implementation: simple
        return this._mappings;
    }

    public set source(value: string) {
        // Method Implementation: simple
        this._source = value;
        this._isSourceDefined = true;
    }

    public set destination(value: string) {
        // Method Implementation: simple
        this._destination = value;
        this._isDestinationDefined = true;
    }

    public set direction(value: MappingDirections) {
        // Method Implementation: simple
        this._direction = value;
        this._isDirectionDefined = true;
    }

    public set restrict(value: boolean) {
        // Method Implementation: simple
        this._restrict = value;
        this._isRestrictDefined = true;
    }

    public set mappings(value: Mapping[]) {
        // Method Implementation: simple
        this._mappings = value;
        this._isMappingsDefined = true;
    }

    private _isSourceDefined: boolean;

    private _isDestinationDefined: boolean;

    private _isDirectionDefined: boolean;

    private _isRestrictDefined: boolean;

    private _isMappingsDefined: boolean;

    public get isSourceDefined(): boolean {
        // Method Implementation: simple
        return this._isSourceDefined;
    }

    public get isDestinationDefined(): boolean {
        // Method Implementation: simple
        return this._isDestinationDefined;
    }

    public get isDirectionDefined(): boolean {
        // Method Implementation: simple
        return this._isDirectionDefined;
    }

    public get isRestrictDefined(): boolean {
        // Method Implementation: simple
        return this._isRestrictDefined;
    }

    public get isMappingsDefined(): boolean {
        // Method Implementation: simple
        return this._isMappingsDefined;
    }

    public constructor();
    public constructor(source: IMapping);
    public constructor(source: Partial<Pick<Mapping, 'source' | 'destination' | 'direction' | 'restrict' | 'mappings'>>);
    public constructor(source?: IMapping | Partial<Pick<Mapping, 'source' | 'destination' | 'direction' | 'restrict' | 'mappings'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isMappingModel(source)) {
                if (source.isSourceDefined) {
                    this._source = source.source;
                    this._isSourceDefined = true;
                }
                else {
                    this._source = '';
                    this._isSourceDefined = false;
                }
                
                if (source.isDestinationDefined) {
                    this._destination = source.destination;
                    this._isDestinationDefined = true;
                }
                else {
                    this._destination = '';
                    this._isDestinationDefined = false;
                }
                
                if (source.isDirectionDefined) {
                    this._direction = source.direction;
                    this._isDirectionDefined = true;
                }
                else {
                    this._direction = MappingDirections.To;
                    this._isDirectionDefined = false;
                }
                
                if (source.isRestrictDefined) {
                    this._restrict = source.restrict;
                    this._isRestrictDefined = true;
                }
                else {
                    this._restrict = false;
                    this._isRestrictDefined = false;
                }
                
                if (source.isMappingsDefined) {
                    this._mappings = source.mappings;
                    this._isMappingsDefined = true;
                }
                else {
                    this._mappings = [];
                    this._isMappingsDefined = true;
                }
            }
            else {
                if (source.source !== undefined) {
                    this._source = source.source;
                    this._isSourceDefined = true;
                }
                else {
                    this._source = '';
                    this._isSourceDefined = false;
                }
                
                if (source.destination !== undefined) {
                    this._destination = source.destination;
                    this._isDestinationDefined = true;
                }
                else {
                    this._destination = '';
                    this._isDestinationDefined = false;
                }
                
                if (source.direction !== undefined) {
                    this._direction = source.direction;
                    this._isDirectionDefined = true;
                }
                else {
                    this._direction = MappingDirections.To;
                    this._isDirectionDefined = false;
                }
                
                if (source.restrict !== undefined) {
                    this._restrict = source.restrict;
                    this._isRestrictDefined = true;
                }
                else {
                    this._restrict = false;
                    this._isRestrictDefined = false;
                }
                
                if (source.mappings !== undefined) {
                    this._mappings = source.mappings;
                    this._isMappingsDefined = true;
                }
                else {
                    this._mappings = [];
                    this._isMappingsDefined = true;
                }
            }
        }
        else {
            this._source = '';
            this._isSourceDefined = false;
        
            this._destination = '';
            this._isDestinationDefined = false;
        
            this._direction = MappingDirections.To;
            this._isDirectionDefined = false;
        
            this._restrict = false;
            this._isRestrictDefined = false;
        
            this._mappings = [];
            this._isMappingsDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Mapping.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isSourceDefined && this.source !== undefined)
            definedProperties.push('source');
        if (this.isDestinationDefined && this.destination !== undefined)
            definedProperties.push('destination');
        if (this.isDirectionDefined && this.direction !== undefined)
            definedProperties.push('direction');
        if (this.isRestrictDefined && this.restrict !== undefined)
            definedProperties.push('restrict');
        if (this.isMappingsDefined && this.mappings !== undefined)
            definedProperties.push('mappings');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'source':
                return this.source;
            case 'destination':
                return this.destination;
            case 'direction':
                return this.direction;
            case 'restrict':
                return this.restrict;
            case 'mappings':
                return this.mappings;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isSourceDefined && this.source !== undefined) {
            if (this.source !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('source', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.source);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('source', 'null'));
            }
        }
        
        if (this.isDestinationDefined && this.destination !== undefined) {
            if (this.destination !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('destination', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.destination);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('destination', 'null'));
            }
        }
        
        if (this.isDirectionDefined && this.direction !== undefined) {
            if (this.direction !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('direction', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.direction);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('direction', 'null'));
            }
        }
        
        if (this.isRestrictDefined && this.restrict !== undefined) {
            if (this.restrict !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('restrict', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.restrict);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('restrict', 'null'));
            }
        }
        
        if (this.isMappingsDefined && this.mappings !== undefined) {
            if (this.mappings !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('mappings', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.mappings.length));
                for (let i = 0; i < this.mappings.length; i++) {
                    const item = this.mappings[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('mappings', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'source':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.source = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'source' cannot be null`);
                    this.source = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'source'`);
                }
        
                return true;
            case 'destination':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.destination = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'destination' cannot be null`);
                    this.destination = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'destination'`);
                }
        
                return true;
            case 'direction':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.direction = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'direction' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'direction'`);
                }
        
                return true;
            case 'restrict':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.restrict = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'restrict' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'restrict'`);
                }
        
                return true;
            case 'mappings':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.mappings = [];
                
                    let item: Mapping = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === Mapping.modelName) {
                                item = new Mapping();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${Mapping.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'mappings' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'mappings'`);
                        }
                
                        this.mappings.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'mappings' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'mappings'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // source
        if (this.isSourceDefined && this.source !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.source === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.source);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // destination
        if (this.isDestinationDefined && this.destination !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.destination === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.destination);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // direction
        if (this.isDirectionDefined && this.direction !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.direction);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // restrict
        if (this.isRestrictDefined && this.restrict !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.restrict);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // mappings
        if (this.isMappingsDefined && this.mappings !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.mappings === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.mappings.length);
            
                for (const item of this.mappings) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // source
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.source = <any>null;
                    break;
                case 1:
                    this.source = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // destination
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.destination = <any>null;
                    break;
                case 1:
                    this.destination = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // direction
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.direction = reader.readEnumValue();
        }
        
        // restrict
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.restrict = reader.readBooleanValue();
        }
        
        // mappings
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: Mapping;
            
                    this.mappings = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new Mapping();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.mappings.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Mapping {
        // Method Implementation: simple
        const schema = MappingZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: MappingZodSchemaShape): Mapping {
        // Method Implementation: parseZodSchema
        return new Mapping({
            source: schema.source,
            destination: schema.destination,
            direction: (() => {
            if (!schema.direction)
                return;
        
            switch (schema.direction) {
                case 'To':
                    return MappingDirections.To;
                case 'From':
                    return MappingDirections.From;
                case 'Both':
                    return MappingDirections.Both;
                default:
                    throw new Error(`Enum type "schema.direction" not recognized`);
            }
        })(),
            restrict: schema.restrict,
            mappings: schema.mappings?.map(i => Mapping.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            source: this.isSourceDefined ? ((this.source === null ? undefined : this.source)) : undefined,
            destination: this.isDestinationDefined ? ((this.destination === null ? undefined : this.destination)) : undefined,
            direction: this.isDirectionDefined ? (MappingDirections[this.direction]) : undefined,
            restrict: this.isRestrictDefined ? (this.restrict) : undefined,
            mappings: this.isMappingsDefined ? (this.mappings?.map(item => item)) : undefined,
        };
    }

    public clone(): Mapping {
        // Method Implementation: clone
        return new Mapping({
            source: this.isSourceDefined ? this.source : undefined,
            destination: this.isDestinationDefined ? this.destination : undefined,
            direction: this.isDirectionDefined ? this.direction : undefined,
            restrict: this.isRestrictDefined ? this.restrict : undefined,
            mappings: this.isMappingsDefined ? this.mappings?.map(i => i.clone()) : undefined
        });
    }
}

export class ModelClassFactory {
    public static createModel(modelName: string) {
        // Method Implementation: createModel
        switch (modelName) {
            case 'Mapper':
                return new Mapper();
            case 'Mapping':
                return new Mapping();
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static createFromModel(modelName: string, source: any) {
        // Method Implementation: createFromModel
        switch (modelName) {
            case 'Mapper':
                return new Mapper(source);
            case 'Mapping':
                return new Mapping(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static parseModel(modelName: string, source: any) {
        // Method Implementation: parseModel
        switch (modelName) {
            case 'Mapper':
                return Mapper.parse(source);
            case 'Mapping':
                return Mapping.parse(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }
}
